import { useCallback, useEffect, useMemo, useState } from 'react';
import { EventStatus } from '@sportspredict/shared';
import { getUserLobbyEliminationRecord } from '@/utils';

interface UseLobbyAccessControlProps {
	eventResponse: any;
	lobbyResponse: any;
	lobbyId: string | undefined;
	user: any;
	isAdmin: boolean;
	isCrowdLockMode: boolean;
}

export function useLobbyAccessControl({
	eventResponse,
	lobbyResponse,
	lobbyId,
	user,
	isAdmin,
	isCrowdLockMode,
}: UseLobbyAccessControlProps) {
	const isEventClosed = useMemo(() => {
		if (!eventResponse) return false;

		const now = new Date();

		return (
			eventResponse.status === EventStatus.Closed ||
			(eventResponse.end_date && new Date(eventResponse.end_date) <= now)
		);
	}, [eventResponse]);

	const [showLobbyClosedPopup, setShowLobbyClosedPopup] = useState(false);

	const hasUserJoined = useCallback(
		(response: any) => {
			if (!response) return false;
			if (!user?.id) return true;
			return response?.lobby_users?.some((userObj: any) => userObj.user_id === user?.id);
		},
		[user?.id]
	);

	const hasCurrentUserJoinedLobby = useMemo(
		() => hasUserJoined(lobbyResponse),
		[hasUserJoined, lobbyResponse]
	);

	const isCrowdLockEliminatedUser = useMemo(() => {
		return !!getUserLobbyEliminationRecord(lobbyResponse, user?.id, lobbyId);
	}, [lobbyResponse, lobbyId, user?.id]);

	const isCrowdLockRevivedUser = useMemo(() => {
		if (!user?.id) return false;

		const revivedUsers = lobbyResponse?.revived_users ?? [];
		return (
			Array.isArray(revivedUsers) && revivedUsers.some((id: any) => String(id) === String(user?.id))
		);
	}, [lobbyResponse, user?.id]);

	const hasCrowdLockLobbyAccess = useMemo(() => {
		if (!isCrowdLockMode) return hasCurrentUserJoinedLobby;

		return hasCurrentUserJoinedLobby || isCrowdLockEliminatedUser || isCrowdLockRevivedUser;
	}, [
		hasCurrentUserJoinedLobby,
		isCrowdLockEliminatedUser,
		isCrowdLockMode,
		isCrowdLockRevivedUser,
	]);

	useEffect(() => {
		if (isEventClosed && !hasCrowdLockLobbyAccess && !isAdmin) {
			setShowLobbyClosedPopup(true);
		}
	}, [isEventClosed, hasCrowdLockLobbyAccess, isAdmin]);

	return {
		isEventClosed,
		showLobbyClosedPopup,
		setShowLobbyClosedPopup,
		hasCurrentUserJoinedLobby,
		isCrowdLockEliminatedUser,
		isCrowdLockRevivedUser,
		hasCrowdLockLobbyAccess,
	};
}
